import ExcelJS from 'exceljs';
import type { CodingVisit } from '../services/rogerDocApi';
import {
  getVisitTypeLabel,
  getMedicalCodingStatusLabel,
  getDocumentStatusBooleans,
  getBranchName,
} from './codingVisitUtils';
import {
  formatDateToZeroPaddedMMDDYYYY,
  formatDateTimeWithTimezoneFallback,
} from './dateUtils';
import type { RogerDocClinic } from '../services/rogerDocApi';

/**
 * Extracts provider first and last name: first/last if present, otherwise username in first.
 */
function getProviderNames(visit: CodingVisit): { first: string; last: string } {
  const v = visit.visit;
  if (v.nurse_first_name != null && v.nurse_last_name != null) {
    return { first: v.nurse_first_name, last: v.nurse_last_name };
  }
  return { first: v.nurse_username ?? '', last: '' };
}

/**
 * Exports selected coding visits to an Excel file with columns matching the
 * table (excluding post coding checks / alert triangles).
 */
export async function exportCodingVisitsToExcel(
  visits: CodingVisit[],
  filenameBase: string,
  options?: {
    includeTherapyEvalTags?: boolean;
    clinics?: RogerDocClinic[];
  }
): Promise<void> {
  const includeTherapyEvalTags = options?.includeTherapyEvalTags === true;
  const clinics = options?.clinics || [];
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Coding Visits');

  const columns = [
    { header: 'Patient Last', key: 'patientLast', width: 20 },
    { header: 'Patient First', key: 'patientFirst', width: 20 },
    { header: 'Medical Record Number', key: 'patientId', width: 22 },
    { header: 'Visit Date', key: 'visitDate', width: 14 },
    { header: 'Visit Type', key: 'visitType', width: 14 },
    { header: 'Clinic', key: 'location', width: 22 },
    { header: 'Branch', key: 'branch', width: 22 },
    { header: 'Insurance', key: 'insurance', width: 32 },
    { header: 'Provider Last', key: 'providerLast', width: 20 },
    { header: 'Provider First', key: 'providerFirst', width: 20 },
    { header: 'PDFs Attached', key: 'pdfsAttached', width: 16 },
    { header: 'Order Attached', key: 'orderAttached', width: 16 },
    { header: 'Order Signed', key: 'orderSigned', width: 14 },
    { header: 'F2F Attached', key: 'f2fAttached', width: 14 },
    { header: 'F2F Signed', key: 'f2fSigned', width: 12 },
    { header: 'Status', key: 'status', width: 24 },
    { header: 'Roger Primary', key: 'medicalCode', width: 14 },
    { header: 'EMR Filled', key: 'emrFilled', width: 12 },
    { header: 'Archived', key: 'archived', width: 10 },
    {
      header: 'Multiple Chronic Conditions',
      key: 'multipleChronicConditions',
      width: 24,
    },
    {
      header: 'Medication Profile Complete',
      key: 'medicationProfileComplete',
      width: 24,
    },
    { header: 'Coding Start Time', key: 'scrapeDatetime', width: 22 },
  ];

  if (includeTherapyEvalTags) {
    // This block can be simplified if we ever launch the
    // medical_coding_consider_therapy_eval flag globally
    // and clean up the flag.

    const therapyEvalColumns = [
      { header: 'Therapy Eval Scheduled', key: 'evalScheduled', width: 20 },
      { header: 'Therapy Eval Completed', key: 'evalCompleted', width: 20 },
    ];

    const f2fSignedIndex = columns.findIndex(
      column => column.key === 'f2fSigned'
    );
    const insertAt =
      f2fSignedIndex === -1 ? columns.length : f2fSignedIndex + 1;

    columns.splice(insertAt, 0, ...therapyEvalColumns);
  }

  worksheet.columns = columns;

  for (const visit of visits) {
    const doc = getDocumentStatusBooleans(visit);
    const v = visit.visit;
    const providerNames = getProviderNames(visit);

    worksheet.addRow({
      patientLast: v.patient_last_name ?? '',
      patientFirst: v.patient_first_name ?? '',
      patientId: v.patient_id ?? '',
      visitDate: formatDateToZeroPaddedMMDDYYYY(v.visit_date_date),
      visitType: getVisitTypeLabel(v.visit_type),
      location: v.location ?? '',
      branch: getBranchName(v.branch_id, clinics) ?? '',
      insurance: visit.medical_data?.demographics?.Insurance
        ? String(visit.medical_data.demographics.Insurance)
        : '',
      providerLast: providerNames.last,
      providerFirst: providerNames.first,
      pdfsAttached: visit.tags?.no_documents ? 'No' : 'Yes',
      orderAttached: doc.orderAttached ? 'Yes' : 'No',
      orderSigned: doc.orderSigned ? 'Yes' : 'No',
      f2fAttached: doc.f2fAttached ? 'Yes' : 'No',
      f2fSigned: doc.f2fSigned ? 'Yes' : 'No',
      ...(includeTherapyEvalTags
        ? {
            evalScheduled: visit.tags?.eval_scheduled ? 'Yes' : 'No',
            evalCompleted: visit.tags?.eval_completed ? 'Yes' : 'No',
          }
        : {}),
      status: getMedicalCodingStatusLabel(visit.status),
      medicalCode: visit.primary_diagnosis?.diagnosis?.name ?? '',
      emrFilled: visit.tags?.EMR_filled ? 'Yes' : 'No',
      archived: visit.tags?.archived ? 'Yes' : 'No',
      multipleChronicConditions: visit.tags?.has_multiple_chronic_conditions
        ? 'Yes'
        : 'No',
      medicationProfileComplete: visit.tags?.medication_profile_complete
        ? 'Yes'
        : 'No',
      scrapeDatetime: formatDateTimeWithTimezoneFallback(
        visit.scrape_datetime,
        'MM/dd/yyyy h:mm a'
      ),
    });
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filenameBase}.xlsx`;
  link.click();
  window.URL.revokeObjectURL(url);
}
